<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6">
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center text-left">
        <h2 class="text-base font-bold text-[#0d4169]">Purchase Requisition (PR)</h2>
        <span v-if="loading" class="text-xs text-slate-400 animate-pulse">Loading data...</span>
      </div>
      <div class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2 text-left">
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">PR Number</label>
            <input
              :value="prNumberDisplay || '-'"
              type="text"
              readonly
              class="w-full px-2.5 py-1.5 border border-[#e2e8f0] rounded-md outline-none text-sm bg-slate-50 text-slate-600 font-semibold cursor-not-allowed"
            >
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Transaction Date</label>
            <div class="relative flex items-center">
              <input
                :value="formatDateForDisplay(form.date)"
                type="text"
                class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none text-sm bg-white"
                readonly
              >
              <input
                v-model="form.date"
                :min="todayDate"
                type="date"
                class="absolute inset-0 w-full h-full opacity-1 bg-transparent border-none outline-none text-transparent cursor-pointer px-2.5 py-1.5 text-sm"
                style="color: transparent;"
                required
              >
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Department</label>
            <select v-model="form.requestedDepartment" @change="handleDeptChange" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer" required>
              <option value="" disabled>Select Department</option>
              <option v-for="dept in userDeptList" :key="dept" :value="dept">{{ dept }}</option>
              <option :value="null" >Stock</option>
            </select>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Category</label>
            <select v-model="form.categoryId" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option :value="null" disabled>Select Category</option>
              <option v-for="cat in selectableCategories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>

            </select>
          </div>
        </div>
      </div>
    </div>

    <div v-if="categoryItems.length > 0" class="flex flex-col gap-4">
      <div class="w-full overflow-x-auto shadow-sm rounded-xl">
        <table class="w-full border-collapse bg-white overflow-hidden">
          <thead>
            <tr>
              <th>No.</th>
              <th>Item Code</th>
              <th>Item Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Min Level</th>
              <th>Current Balance</th>
              <th>Unit Price</th>
              <th>Total Price</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in categoryItems" :key="item.id" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ index + 1 }}</td>
              <td>{{ item.itemCode || '-' }}</td>
              <td>{{ item.itemName || '-' }}</td>
              <td>{{ item.category?.name || '-' }}</td>
              <td>{{ item.unit || '-' }}</td>
              <td>{{ item.minStock ?? 0 }}</td>
              <td>
                <span class="px-2 py-0.5 rounded text-[11px] font-bold border" :class="(currentBalanceByItemId[item.id] || 0) > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'">
                  {{ currentBalanceByItemId[item.id] ?? 0 }}
                </span>
              </td>
              <td>{{ getItemRecentUnitPrice(item).toFixed(2) }}</td>
              <td>{{ (getItemRecentUnitPrice(item) * (quantityByItemId[item.id] || 0)).toFixed(2) }}</td>
              <td>
                <input  v-model.number="quantityByItemId[item.id]" type="number" min="0"    step="any"
                inputmode="decimal" placeholder="0" class="w-16 px-2 py-0 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] text-sm">
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex justify-end px-2 pt-2">
        <button @click="saveToDatabase" :disabled="isSaving" class="px-5 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-50 flex items-center gap-2">
          <div
  v-if="isSaving"
  class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
></div>
         <span v-if="!isSaving">Save Requisition</span>
        </button>
      </div>
    </div>

    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center text-left">
        <h2 class="text-base font-bold text-[#0d4169]">My Requisitions</h2>
        <span v-if="historyLoading" class="text-xs text-slate-400 animate-pulse">Loading...</span>
      </div>
      <div class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-2 text-left">
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">From <span class="text-red-500">*</span></label>
            <div class="relative flex items-center">
              <input
                :value="formatDateForDisplay(historyFilters.fromDate)"
                type="text"
                class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none text-sm bg-white"
                readonly
              >
              <input
                v-model="historyFilters.fromDate"
                type="date"
                class="absolute inset-0 w-full h-full opacity-1 bg-transparent border-none outline-none text-transparent cursor-pointer px-2.5 py-1.5 text-sm"
                style="color: transparent;"
              >
            </div>
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Upto</label>
            <div class="relative flex items-center">
              <input
                :value="historyFilters.toDate ? formatDateForDisplay(historyFilters.toDate) : ''"
                type="text"
                placeholder="-"
                class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none text-sm bg-white"
                readonly
              >
              <input
                v-model="historyFilters.toDate"
                type="date"
                :min="historyFilters.fromDate || undefined"
                class="absolute inset-0 w-full h-full opacity-1 bg-transparent border-none outline-none text-transparent cursor-pointer px-2.5 py-1.5 text-sm"
                style="color: transparent;"
              >
            </div>
          </div>
          <div class="flex flex-col gap-1 min-w-0">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Department</label>
            <div class="flex items-center gap-2 lg:block">
              <select v-model="historyFilters.requestedDepartment" class="flex-1 min-w-0 w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
                <option value="">All Departments</option>
                <option v-for="dept in userDeptList" :key="`hist-${dept}`" :value="dept">{{ dept }}</option>
                <option value="__STOCK__">Stock</option>
              </select>
              <button
                type="button"
                @click="handlePrintRequisitions"
                class="shrink-0 lg:hidden px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
                :disabled="!canPrintRequisitions"
                title="Print is available when all listed requisitions are Accepted"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M6 9V2h12v7"/>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
                <span>Print</span>
              </button>
            </div>
          </div>
          <div class="flex flex-col gap-1 min-w-0">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Status</label>
            <select v-model="historyFilters.status" class="w-full px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option value="all">All</option>
              <option value="accepted">Accepted</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div class="hidden lg:flex flex-col justify-end items-end">
            <button
              type="button"
              @click="handlePrintRequisitions"
              class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
              :disabled="!canPrintRequisitions"
              title="Print is available when all listed requisitions are Accepted"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9V2h12v7"/>
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                <rect x="6" y="14" width="12" height="8"/>
              </svg>
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>
      <div class="px-6 pb-4">
        <div class="w-full overflow-x-auto shadow-sm rounded-xl">
          <table class="w-full border-collapse bg-white overflow-hidden history-table">
            <thead>
              <tr>
                <th>#</th>
                <th>PR No</th>
                <th>Date</th>
                <th>Department</th>
                <th>Requested By</th>
                <th>Category</th>
                <th>Item</th>
                <th>Unit</th>
                <th class="qty-col">Req. Qty</th>
                <th class="qty-col">Appr. Qty</th>
                <th>U. Price</th>
                <th>T. Price</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in filteredHistoryRows" :key="row.id" class="hover:bg-slate-100/50 transition-colors border-b">
                <td>{{ index + 1 }}</td>
                <td>{{ row.prNo || '-' }}</td>
                <td>{{ row.date ? formatDateForDisplay(new Date(row.date).toISOString().slice(0, 10)) : '-' }}</td>
                <td>{{ row.departmentName || '-' }}</td>
                <td>{{ row.requestedBy || '-' }}</td>
                <td>{{ row.categoryName || '-' }}</td>
                <td>{{ row.itemName || '-' }}</td>
                <td>{{ row.unit || '-' }}</td>
                <td class="qty-col">{{ row.quantity }}</td>
                <td class="qty-col">{{ row.approvedQty }}</td>
                <td>{{ Number(row.unitPrice || 0).toFixed(2) }}</td>
                <td>{{ Number(row.totalPrice || 0).toFixed(2) }}</td>
                <td>
                  <span class="px-2 py-0.5 rounded text-[11px] font-bold border" :class="getApprovalStatusClass(row.isApproved)">
                    {{ getApprovalStatusLabel(row.isApproved) }}
                  </span>
                </td>
              </tr>
              <tr v-if="!historyLoading && historyFilters.fromDate && filteredHistoryRows.length === 0">
                <td colspan="13" class="text-center text-slate-400 italic py-8">No requisitions found for the selected filters.</td>
              </tr>
              <tr v-if="!historyFilters.fromDate">
                <td colspan="13" class="text-center text-slate-400 italic py-8">Select a From date to view your requisitions.</td>
              </tr>
            </tbody>
            <tfoot v-if="filteredHistoryRows.length > 0">
              <tr class="history-footer">
                <td colspan="9"></td>
                <td colspan="2" class="align-top history-percent-cell">
                  <div class="flex items-center gap-1">
                    <label class="text-[0.72rem] font-semibold whitespace-nowrap">%</label>
                    <input
                      v-model="historyPercent"
                      type="number"
                      min="0"
                      step="any"
                      inputmode="decimal"
                      placeholder="0"
                      class="history-percent-input px-1.5 py-0.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] text-sm"
                    >
                  </div>
                </td>
                <td class="align-top">
                  <div>Base Amount: {{ historyBaseAmount.toFixed(2) }}</div>
                  <div v-if="hasHistoryGrandTotal" class="text-[#0d4169] font-bold mt-0.5">
                    Grand total: {{ historyGrandTotal.toFixed(2) }}
                  </div>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const categories = ref([])
const registeredItems = ref([])
const departments = ref([])
const loading = ref(false)
const isSaving = ref(false)
const quantityByItemId = ref({})
const currentBalanceByItemId = ref({})
const historyRows = ref([])
const historyLoading = ref(false)
const historyPercent = ref('')
const todayDate = new Date().toISOString().slice(0, 10)

// Display-only PR number fetched from backend.
// This is intentionally NOT sent in the save/update payload.
const prNumberDisplay = ref('')

const form = ref({
  prNo: '',
  date: new Date().toISOString().slice(0, 10),
  requestedDepartment: '',
  departmentId: null,
  categoryId: null,
  remark: ''
})

const historyFilters = ref({
  fromDate: '',
  toDate: '',
  requestedDepartment: '',
  status: 'all'
})

function getApprovalStatusKey(status) {
  const n = Number(status ?? 0)
  if (n === 1) return 'accepted'
  if (n === 2) return 'rejected'
  return 'pending'
}

const filteredHistoryRows = computed(() => {
  const rows = historyRows.value || []
  const status = String(historyFilters.value.status || 'all').toLowerCase()
  if (!status || status === 'all') return rows
  return rows.filter((row) => getApprovalStatusKey(row.isApproved) === status)
})

const historyBaseAmount = computed(() =>
  (filteredHistoryRows.value || []).reduce((sum, row) => sum + Number(row.totalPrice || 0), 0)
)

const historyGrandTotal = computed(() => {
  const base = historyBaseAmount.value
  const pct = Number(historyPercent.value)
  if (!Number.isFinite(pct) || pct === 0) return base
  return base + (base * pct / 100)
})

const historyPercentAmount = computed(() => {
  const base = historyBaseAmount.value
  const pct = Number(historyPercent.value)
  if (!Number.isFinite(pct) || pct === 0) return 0
  return base * pct / 100
})

const hasHistoryGrandTotal = computed(() => {
  const pct = Number(historyPercent.value)
  return historyPercent.value !== '' && Number.isFinite(pct) && pct !== 0
})

const allFilteredAreAccepted = computed(() =>
  filteredHistoryRows.value.length > 0 &&
  filteredHistoryRows.value.every((row) => Number(row.isApproved ?? 0) === 1)
)

const canPrintRequisitions = computed(() =>
  allFilteredAreAccepted.value &&
  !historyLoading.value &&
  !!historyFilters.value.fromDate
)

function formatDateForDisplay(isoDate) {
  const s = String(isoDate || '')
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return s
  return `${m[3]}/${m[2]}/${m[1]}`
}


const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)

const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)

const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

const selectableCategories = computed(() => categories.value)

const userDeptList = computed(() => {
  const rawPriv = currentUser.value?.privilege
  let privilege = {}
  if (rawPriv) {
    try { privilege = typeof rawPriv === 'string' ? JSON.parse(rawPriv) : rawPriv } catch { privilege = {} }
  }
  const hasAll = privilege?.all === true || privilege?.All === true
  if (hasAll) return departments.value.map(d => d.name).filter(Boolean)
  const depts = currentUser.value?.departments || currentUser.value?.department?.name || currentUser.value?.departmentName || currentUser.value?.dept
  if (!depts) return []
  if (typeof depts === 'string') return depts.split(',').map(d => d.trim()).filter(Boolean)
  if (Array.isArray(depts)) return depts.filter(Boolean)
  return [depts].filter(Boolean)
})

const categoryItems = computed(() => {
  if (!form.value.categoryId) return []
  return (registeredItems.value || [])
    .filter(i => i.categoryId === form.value.categoryId)
    .slice()
    .sort((a, b) => String(a?.itemName || '').localeCompare(String(b?.itemName || ''), undefined, { sensitivity: 'base' }))
})

function toFiniteNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function normalizeUnit(unit) {
  return String(unit || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9.]/g, '')
}

function isLiterOrKgUnit(unit) {
  const u = normalizeUnit(unit)
  return (
    u === 'l' ||
    u === 'liter' ||
    u === 'liters' ||
    u.startsWith('liter') ||
    u === 'kg' ||
    u === 'kilogram' ||
    u === 'k.g' ||
    u === 'k.g.'
  )
}

function computeDerivedUnitPrice(unit, recentUnitPrice, recentVolume) {
  const price = Number(recentUnitPrice ?? 0)
  if (!isLiterOrKgUnit(unit)) return price

  const volume = Number(recentVolume ?? 0)
  if (!(volume > 0)) return 0

  return (price / volume) * 1000
}

function getItemRecentUnitPrice(item) {
  if (!item) return 0
  // Pricing rule (aligned with itemPage.vue Recent Price):
  // - Liter/K.G. => (recent_unit_price / recent_volume) * 1000
  // - others     => recent_unit_price
  const recentUnitPrice = item?.recent_unit_price ?? item?.recentUnitPrice ?? null
  const recentVolume = item?.recent_volume ?? item?.recentVolume ?? null
  return computeDerivedUnitPrice(item?.unit, recentUnitPrice, recentVolume)
}

function handleDeptChange() {
  const matched = departments.value.find(d => d.name?.toLowerCase() === String(form.value.requestedDepartment || '').toLowerCase())
  form.value.departmentId = matched ? matched.id : null
  form.value.remark = matched ? '' : (form.value.requestedDepartment == null ? 'Stock' : '')
}

function resolveHistoryDepartmentId() {
  const selected = historyFilters.value.requestedDepartment
  if (!selected || selected === '__STOCK__') return null
  const matched = departments.value.find(d =>
    d.name?.toLowerCase() === String(selected).toLowerCase()
  )
  return matched?.id ?? null
}

function getApprovalStatusLabel(status) {
  const n = Number(status ?? 0)
  if (n === 1) return 'Accepted'
  if (n === 2) return 'Rejected'
  return 'Pending'
}

function getApprovalStatusClass(status) {
  const n = Number(status ?? 0)
  if (n === 1) return 'bg-emerald-50 text-emerald-700 border-emerald-200'
  if (n === 2) return 'bg-red-50 text-red-600 border-red-200'
  return 'bg-amber-50 text-amber-700 border-amber-200'
}

function getHistoryDepartmentLabel() {
  const selected = historyFilters.value.requestedDepartment
  if (!selected) return 'All Departments'
  if (selected === '__STOCK__') return 'Stock'
  return selected
}

function getHistoryDateRangeLabel() {
  const from = historyFilters.value.fromDate
  if (!from) return '-'
  const fromLabel = formatDateForDisplay(from)
  const to = historyFilters.value.toDate
  return to ? `${fromLabel} - ${formatDateForDisplay(to)}` : fromLabel
}

function getImageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'PNG'
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG'
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP'
  return 'PNG'
}

async function imageUrlToDataUrl(url) {
  if (!url) return { dataUrl: null, format: 'PNG' }
  try {
    const res = await fetch(url)
    if (!res.ok) return { dataUrl: null, format: 'PNG' }
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return {
      dataUrl,
      format: getImageFormatFromDataUrl(dataUrl)
    }
  } catch {
    return { dataUrl: null, format: 'PNG' }
  }
}

async function generatePrRequisitionPdf() {
  const rows = filteredHistoryRows.value || []
  if (!rows.length) return

  const totals = {
    baseAmount: Number(historyBaseAmount.value || 0),
    percentAmount: Number(historyPercentAmount.value || 0),
    grandTotal: Number(historyGrandTotal.value || 0),
    hasPercentAdjustment: hasHistoryGrandTotal.value
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const leftMargin = 10
  const rightMargin = 8
  const topMargin = 8
  const headerBottomY = 28
  const totalPagesExp = '{total_pages_count_string}'
  const printDateLabel = getHistoryDateRangeLabel()
  const { dataUrl: logoDataUrl, format: logoFormat } = await imageUrlToDataUrl(String(printCompanyLogo.value || '').trim())

  const tableBody = rows.map((row, idx) => ([
    String(idx + 1),
    row.prNo || '-',
    row.date ? formatDateForDisplay(new Date(row.date).toISOString().slice(0, 10)) : '-',
    row.departmentName || '-',
    row.requestedBy || '-',
    row.categoryName || '-',
    row.itemName || '-',
    row.unit || '-',
    String(row.quantity ?? '-'),
    String(row.approvedQty ?? '-'),
    Number(row.unitPrice || 0).toFixed(2),
    Number(row.totalPrice || 0).toFixed(2),
    getApprovalStatusLabel(row.isApproved)
  ]))

  autoTable(doc, {
    head: [[
      '#', 'PR No', 'Date', 'Department', 'Requested By', 'Category', 'Item',
      'Unit', 'Req. Qty', 'Appr. Qty', 'U. Price', 'T. Price', 'Status'
    ]],
    body: tableBody,
    startY: headerBottomY + 2,
    theme: 'grid',
    margin: { left: leftMargin, right: rightMargin, top: headerBottomY + 2, bottom: 12 },
    styles: {
      fontSize: 7.5,
      cellPadding: 1.0,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [0, 0, 0],
      fontStyle: 'bold'
    },
    bodyStyles: {
      fillColor: [255, 255, 255]
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255]
    },
    didDrawPage: () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, logoFormat, leftMargin, topMargin, 14, 10, undefined, 'FAST')
      }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text(String(printCompanyName.value || 'Company'), pageWidth / 2, topMargin + 3, { align: 'center' })
      doc.setFontSize(11)
      doc.text('Purchase Requisition', pageWidth / 2, topMargin + 8, { align: 'center' })

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.text(`Date: ${printDateLabel}`, pageWidth - rightMargin, topMargin + 6, { align: 'right' })
      doc.text(`Department: ${getHistoryDepartmentLabel()}`, pageWidth - rightMargin, topMargin + 11, { align: 'right' })

      doc.setDrawColor(0, 0, 0)
      doc.setLineWidth(0.2)
      doc.line(leftMargin, headerBottomY, pageWidth - rightMargin, headerBottomY)

      const pageNo = doc.getNumberOfPages()
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.text(`Page ${pageNo} of ${totalPagesExp}`, pageWidth - rightMargin, pageHeight - 5, { align: 'right' })
    }
  })

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp)
  }

  const finalY = (doc.lastAutoTable?.finalY || (headerBottomY + 2)) + 8
  const valueX = pageWidth - rightMargin
  const labelX = valueX - 32
  const lineGap = 6
  let totalsY = finalY

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('Base Amount:', labelX, totalsY, { align: 'right' })
  doc.text(Number(totals.baseAmount || 0).toFixed(2), valueX, totalsY, { align: 'right' })

  if (totals.hasPercentAdjustment) {
    totalsY += lineGap
    doc.text(`(${historyPercent.value}%):`, labelX, totalsY, { align: 'right' })
    doc.text(Number(totals.percentAmount || 0).toFixed(2), valueX, totalsY, { align: 'right' })
  }

  totalsY += lineGap
  doc.setLineWidth(0.2)
  doc.line(labelX - 18, totalsY - (lineGap * 0.5), valueX, totalsY - (lineGap * 0.5))
  doc.setFont('helvetica', 'bold')
  doc.text('Grand total:', labelX, totalsY, { align: 'right' })
  doc.text(Number(totals.grandTotal || 0).toFixed(2), valueX, totalsY, { align: 'right' })

  const footerY = Math.max(totalsY + (lineGap * 4), pageHeight - 28)
  const firstX = leftMargin + 10
  const secondX = pageWidth / 2 - 20
  const thirdX = pageWidth - rightMargin - 56
  const lineW = 40

  doc.setFont('helvetica', 'normal')
  doc.line(firstX, footerY, firstX + lineW, footerY)
  doc.text('Requested By', firstX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(secondX, footerY, secondX + lineW, footerY)
  doc.text('Checked By', secondX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(thirdX, footerY, thirdX + lineW, footerY)
  doc.text('Approved By', thirdX + (lineW / 2), footerY + 5, { align: 'center' })

  const safeDate = String(new Date().toISOString().slice(0, 10)).replace(/[\\/:*?"<>|]/g, '-').trim()
  const filename = `PR-Requisitions-${safeDate}.pdf`
  doc.save(filename)
}

async function handlePrintRequisitions() {
  if (!canPrintRequisitions.value) return
  if (!confirm('Are you sure..........Print?')) return
  await generatePrRequisitionPdf()
}

async function loadMyRequisitionItems() {
  if (!historyFilters.value.fromDate) {
    historyRows.value = []
    return
  }
  if (!companyId.value && !branchId.value) return

  historyLoading.value = true
  try {
    const queryParts = []
    if (branchId.value) queryParts.push(`branchId=${branchId.value}`)
    else if (companyId.value) queryParts.push(`companyId=${companyId.value}`)
    const effectiveToDate = historyFilters.value.toDate || historyFilters.value.fromDate
    queryParts.push(`fromDate=${historyFilters.value.fromDate}`)
    queryParts.push(`toDate=${effectiveToDate}`)

    const deptFilter = historyFilters.value.requestedDepartment
    if (deptFilter === '__STOCK__') {
      queryParts.push('stock=true')
    } else if (deptFilter) {
      const departmentId = resolveHistoryDepartmentId()
      if (departmentId) queryParts.push(`departmentId=${departmentId}`)
    }

    const res = await apiFetch(`/purchase-requisition/my-items?${queryParts.join('&')}`)
    historyRows.value = res.ok ? await res.json().catch(() => []) : []
    historyPercent.value = ''
  } catch {
    historyRows.value = []
  } finally {
    historyLoading.value = false
  }
}

async function loadData() {
  if (!companyId.value && !branchId.value) return
  loading.value = true
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const [catRes, itemRes, depRes] = await Promise.all([
      apiFetch(`/categories?${query}`),
      apiFetch(`/items?${query}`),
      apiFetch(`/departments?${query}`)
    ])
    if (catRes.ok) categories.value = await catRes.json().catch(() => [])
    categories.value = categories.value.filter(c => c.status === 1);
  categories.value.sort((a, b) => a.name.localeCompare(b.name))
    if (itemRes.ok) {
      const items = await itemRes.json().catch(() => [])
      // Ensure pricing snapshot fields exist as soon as items are loaded.
      registeredItems.value = (Array.isArray(items) ? items : []).map((it) => ({
        ...it,
        recent_unit_price: it?.recent_unit_price ?? null,
        recent_volume: it?.recent_volume ?? null,
      }))
    }
    if (depRes.ok) departments.value = await depRes.json().catch(() => [])
    departments.value = departments.value.filter(d => d.status === 1);
  departments.value.sort((a, b) => a.name.localeCompare(b.name))

  } finally {
    loading.value = false
  }
}

async function loadCategoryBalances() {
  const ids = categoryItems.value.map(i => i.id)
  const map = {}
  await Promise.all(ids.map(async (id) => {
    try {
      const res = await apiFetch(`/items/${id}/stock-balance`)
      if (!res.ok) return (map[id] = 0)
      const data = await res.json().catch(() => ({ data: [] }))
      const rows = Array.isArray(data?.data) ? data.data : []
      map[id] = Math.round(rows.reduce((s, r) => s + Number(r.balance || 0), 0) * 100) / 100
    } catch {
      map[id] = 0
    }
  }))
  currentBalanceByItemId.value = map
}

async function saveToDatabase() {
  const isStockSelected = form.value.requestedDepartment == null
  if (!form.value.departmentId && !isStockSelected) {
    alert('Please select department')
    return
  }
  if (!form.value.categoryId) {
    alert('Please select category')
    return
  }
  const items = categoryItems.value
    .map(i => ({
      itemId: i.id,
      quantity: toFiniteNumber(quantityByItemId.value[i.id], 0),
      unitPrice: getItemRecentUnitPrice(i)
    }))
    .filter(i => Number.isFinite(i.quantity) && i.quantity > 0)
  if (!items.length) {
    alert('Please fill at least one quantity greater than 0')
    return
  }

  const confirmed = confirm("Are you sure..........Save?")
  if (!confirmed) return



  isSaving.value = true
  try {
    const res = await apiFetch('/purchase-requisition', {
      method: 'POST',
      body: JSON.stringify({
        date: form.value.date,
        departmentId: form.value.departmentId,
        remark: form.value.remark,
        companyId: companyId.value,
        branchId: branchId.value,
        created_by: userId.value,
        items
      })
    })
    if (res.ok) {
      const responseData = await res.json().catch(() => ({}))
      const savedPrNo = responseData?.prNo || responseData?.data?.prNo || ''
      quantityByItemId.value = {}
      form.value.categoryId = null
      currentBalanceByItemId.value = {}
      alert(savedPrNo ? `Requisition saved successfully. PR Number: ${savedPrNo}` : 'Requisition saved successfully.')
      // Refresh next PR number for the next entry.
      await fetchNextPrNumberForDisplay()
      if (historyFilters.value.fromDate) await loadMyRequisitionItems()
    } else {
      const err = await res.json().catch(() => ({}))
      alert(err.message || 'Failed to save requisition')
    }
  } catch (err) {
    alert('Failed to save requisition')
  } finally {
    isSaving.value = false
  }
}

async function fetchNextPrNumberForDisplay() {
  try {
    if (!companyId.value && !branchId.value) return
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/purchase-requisition/next-number?${query}`)
    if (!res?.ok) return
    const data = await res.json().catch(() => ({}))
    const nextNumber = data?.nextNumber || ''
    // Display-only: do not use this value in save/update payloads.
    prNumberDisplay.value = nextNumber
  } catch (e) {
    console.error('Error fetching PR number for display:', e)
  }
}

onMounted(async () => {
  await loadData()
  if (userDeptList.value.length === 1) {
    form.value.requestedDepartment = userDeptList.value[0]
    handleDeptChange()
  }
  await fetchNextPrNumberForDisplay()
})

watch(() => form.value.categoryId, async () => {
  quantityByItemId.value = {}
  await loadCategoryBalances()
})

watch(
  () => [historyFilters.value.fromDate, historyFilters.value.toDate, historyFilters.value.requestedDepartment],
  async () => {
    await loadMyRequisitionItems()
  }
)
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.82rem;
  color: #1e293b;
}
th, td {
  padding: 5px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.72rem;
  white-space: nowrap;
}
th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}
tbody tr:hover {
  background: #f1f5f9;
}
.history-table th.qty-col,
.history-table td.qty-col {
  text-align: center;
}
.history-footer td {
  background: #f8fafc;
  border-top: 2px solid #cbd5e1;
  font-weight: 600;
  vertical-align: top;
}
.history-percent-cell {
  min-width: 8rem;
}
.history-percent-input {
  width: 100%;
  min-width: 5rem;
  max-width: 10rem;
}
</style>
